import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { ThemeColors, useTheme } from '../../context/ThemeContext';
import {
  allergenText, dayLabel, isAllergyReason, isPlannerToday, menuOrderText, menuWeekLabel, sameDayCutOffNote, slotTimeText,
} from '../../utils/canteenPlanner';
import type { CanteenPlanner, CanteenPlannerDay, CanteenPlannerOption } from '../../types/wallet';
import { Notice } from '../wallet/WalletBits';
import { money } from '@shared/commerce';

/**
 * The canteen menu for one child (owner, 2 Nov 2026: "parent should be able to see the menu in the
 * app"): the chosen week day by day - each meal with its serving time, until when it can be ordered,
 * and its dishes at this child's price with veg and allergen tags - then the Extras. Read only; Order
 * this day opens the planner on that day. The same planner data as ordering, so it never disagrees.
 */
export function CanteenMenu({ planner, days, childName, onOrderDay }: {
  planner: CanteenPlanner;
  days: CanteenPlannerDay[];
  childName: string;
  onOrderDay: (date: string) => void;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const twoUp = width >= 900;
  const cur = planner.currency || 'USD';
  // The Extras are the same every school day, so they are listed once.
  const extras = days.find(d => (d.extras ?? []).length > 0)?.extras ?? [];
  const note = sameDayCutOffNote(planner);

  if (days.length === 0) {
    return <View style={styles.card}><Text style={styles.muted}>No data</Text></View>;
  }

  return (
    <View style={styles.stack} testID="canteen-menu">
      <Text style={styles.small}>
        {`${planner.priceLabel} - the prices ${childName} pays.${planner.deliveryText ? ` ${planner.deliveryText}.` : ''}${note ? ` ${note}` : ''}`}
      </Text>
      <View style={[styles.days, twoUp && styles.daysWide]}>
        {days.map(day => {
          const isToday = isPlannerToday(day.date, planner.today);
          const canOrder = day.isOrderable && day.slots.some(s => s.isOpen && !s.ordered);
          return (
            <View key={day.date} style={[styles.card, styles.day, twoUp && styles.dayWide, isToday && styles.dayToday]}
              testID={`canteen-menu-day-${day.date}`}>
              <View style={styles.rowBetween}>
                <View style={styles.dayTitleRow}>
                  <Text style={styles.dayTitle}>{dayLabel(day.date)}</Text>
                  {isToday && <Text style={styles.todayTag}>Today</Text>}
                </View>
                {!!menuWeekLabel(day.cycleWeek) && <Text style={styles.small}>{menuWeekLabel(day.cycleWeek)}</Text>}
              </View>
              {day.closedReason && day.closedReason !== 'Past' && day.slots.length === 0 && <Notice>{day.closedReason}</Notice>}
              {day.slots.map(slot => (
                <View key={slot.slotId} style={styles.meal} testID={`canteen-menu-${day.date}-${slot.slotId}`}>
                  <View style={styles.rowBetween}>
                    <Text style={styles.mealName}>{slot.name}</Text>
                    {!!slotTimeText(slot) && <Text style={styles.small}>{slotTimeText(slot)}</Text>}
                  </View>
                  {!!menuOrderText(day, slot) && (
                    <Text style={[styles.cut, { color: slot.isOpen ? colors.warning : colors.textMuted }]}>{menuOrderText(day, slot)}</Text>
                  )}
                  {slot.ordered && (
                    <View style={styles.orderedRow}>
                      <FeatherIcon name="check-circle" size={12} color={colors.success} />
                      <Text style={[styles.small, { color: colors.success }]}>{`Ordered: ${slot.ordered.itemName}`}</Text>
                    </View>
                  )}
                  {slot.options.length === 0 && <Text style={styles.muted}>No data</Text>}
                  {slot.options.map(option => (
                    <Dish key={option.optionNo} option={option} label={slot.options.length > 1 ? `Option ${option.optionNo}` : null}
                      currency={cur} styles={styles} colors={colors} />
                  ))}
                </View>
              ))}
              {day.closedReason && day.closedReason !== 'Past' && day.slots.length > 0 && (
                <Text style={styles.small}>{day.closedReason}</Text>
              )}
              {canOrder && (
                <TouchableOpacity style={styles.orderBtn} onPress={() => onOrderDay(day.date)} accessibilityRole="button"
                  testID={`canteen-menu-order-${day.date}`}>
                  <FeatherIcon name="shopping-bag" size={13} color={colors.primary} />
                  <Text style={[styles.orderText, { color: colors.primary }]}>{isToday ? 'Order for today' : 'Order this day'}</Text>
                </TouchableOpacity>
              )}
            </View>
          );
        })}
      </View>
      {extras.length > 0 && (
        <View style={[styles.card, styles.gap6]} testID="canteen-menu-extras">
          <Text style={styles.mealName}>Extras</Text>
          <Text style={styles.small}>Drinks and snacks on every school day, up to 5 of one item a day.</Text>
          {extras.map(item => (
            <Dish key={item.itemId} option={item} label={null} currency={cur} styles={styles} colors={colors} />
          ))}
        </View>
      )}
    </View>
  );
}

type Styles = ReturnType<typeof createStyles>;

function Dish({ option, label, currency, styles, colors }: {
  option: CanteenPlannerOption; label: string | null; currency: string; styles: Styles; colors: ThemeColors;
}) {
  const priced = option.price !== null && option.price !== undefined;
  const allergy = isAllergyReason(option.blockedReason);
  const contains = allergenText(option.allergens);
  return (
    <View style={styles.dish} testID={`canteen-menu-dish-${option.itemId}`}>
      <View style={styles.fill}>
        <View style={styles.tagRow}>
          {label && <Text style={styles.small}>{label}</Text>}
          {(option.isVegan || option.isVeg) && (
            <View style={[styles.tag, { backgroundColor: colors.successLight }]}>
              <Text style={[styles.tagText, { color: colors.success }]}>{option.isVegan ? 'Vegan' : 'Veg'}</Text>
            </View>
          )}
          {allergy && (
            <View style={[styles.tag, { backgroundColor: colors.dangerLight }]}>
              <FeatherIcon name="alert-triangle" size={10} color={colors.danger} />
              <Text style={[styles.tagText, { color: colors.danger }]}>Allergy</Text>
            </View>
          )}
        </View>
        <Text style={styles.dishName}>{option.name}</Text>
        {!!option.description && <Text style={styles.small} numberOfLines={2}>{option.description}</Text>}
        {!!contains && <Text style={styles.small}>{contains}</Text>}
        {option.blockedReason && (
          <Text style={[styles.small, { color: allergy ? colors.danger : colors.textSecondary }]}>{option.blockedReason}</Text>
        )}
        {option.warning && <Text style={[styles.small, { color: colors.warning }]}>{option.warning}</Text>}
        {option.changeNote && <Text style={styles.small}>{option.changeNote}</Text>}
      </View>
      <Text style={[styles.price, { color: priced ? colors.primary : colors.textMuted }]}>
        {priced ? money(option.price, currency) : 'Not priced'}
      </Text>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  stack: { gap: 10 },
  fill: { flex: 1, gap: 2 },
  gap6: { gap: 6 },
  card: { backgroundColor: colors.card, borderRadius: 16, borderWidth: 1, borderColor: colors.border, padding: 12 },
  days: { gap: 10 },
  daysWide: { flexDirection: 'row', flexWrap: 'wrap' },
  day: { gap: 8 },
  dayWide: { flexBasis: '48%', flexGrow: 1 },
  dayToday: { borderColor: colors.primary, borderWidth: 1.5 },
  dayTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dayTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  todayTag: { fontSize: 9.5, fontWeight: '800', letterSpacing: 0.4, textTransform: 'uppercase', color: colors.primary },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  meal: { gap: 4, paddingTop: 8, borderTopWidth: 1, borderTopColor: colors.border },
  mealName: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary },
  cut: { fontSize: 11, fontWeight: '700' },
  orderedRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dish: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 4 },
  dishName: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  price: { fontSize: 12.5, fontWeight: '800' },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
  tag: { flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 999, paddingHorizontal: 6, paddingVertical: 1 },
  tagText: { fontSize: 10, fontWeight: '800' },
  muted: { fontSize: 12.5, color: colors.textSecondary },
  small: { fontSize: 11.5, color: colors.textSecondary },
  orderBtn: {
    alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2,
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1, borderColor: colors.primary,
  },
  orderText: { fontSize: 12.5, fontWeight: '800' },
});
